import { Injectable } from '@angular/core';
import {
  StatefulService
} from '@core/services/stateful-service-management/stateful-service-interface/stateful-service-interface';

@Injectable({
  providedIn: 'root'
})
export class WeeklyPlannerCardResizeService implements StatefulService {
  constructor() {}

  // Properties that can be used to track the state of the card while being resized
  private resizing: boolean = false;
  private startX: number;
  private startWidth: number;
  private minWidth: number;
  private maxWidth: number;
  private plannerCardId: string;
  private plannerCardContents: HTMLElement;
  private resizingSide: 'left' | 'right' | null = null;
  private leftEdgeOfMonday: number;
  private rightEdgeOfFriday: number;
  private currentRow: HTMLElement;
  private currentRowDays: NodeListOf<HTMLElement>;
  private minWidthBuffer = 10;
  private backgroundColorFull = null;
  private backgroundColorLight = null;
  private borderColorFull = null;
  private borderColorLight = null;
  private resizableContainer: HTMLElement;

  resetAllValues() {
    this.resizing = false;
    this.startX = null;
    this.startWidth = null;
    this.minWidth = null;
    this.maxWidth = null;
    this.plannerCardId = null;
    this.plannerCardContents = null;
    this.resizingSide = null;
    this.leftEdgeOfMonday = null;
    this.rightEdgeOfFriday = null;
    this.currentRow = null;
    this.currentRowDays = null;
  }

  setupResizeVariables(plannerCardId: string, backgroundColorFull: RegExpMatchArray, backgroundColorLight: string, borderColorFull: RegExpMatchArray, borderColorLight: string) {
    this.plannerCardId = plannerCardId;
    this.backgroundColorFull = backgroundColorFull;
    this.backgroundColorLight = backgroundColorLight;
    this.borderColorFull = borderColorFull;
    this.borderColorLight = borderColorLight;
  }

  onResizeMouseDown(clickEvent: MouseEvent, plannerCardId: string, backgroundColorFull: RegExpMatchArray, backgroundColorLight: string, borderColorFull: RegExpMatchArray, borderColorLight: string, isLeftDrag: boolean): void {
    this.setupResizeVariables(
      plannerCardId,
      backgroundColorFull,
      backgroundColorLight,
      borderColorFull,
      borderColorLight
    );

    clickEvent.stopPropagation();
    clickEvent.preventDefault();

    // Initialize resizing event
    this.resizing = true;
    this.startX = clickEvent.clientX;

    let clickTarget = clickEvent.target as HTMLElement;
    clickTarget = clickTarget.closest('[data-draggable]');
    const parentRow = clickTarget.parentElement;
    const parentRowWidth = parentRow?.offsetWidth;

    this.minWidth = parentRowWidth;

    this.plannerCardContents = document.querySelector(`#card-contents-${plannerCardId}`) as HTMLElement;
    this.currentRow = this.plannerCardContents.parentElement?.parentElement;

    // get day cells for current row being resized
    if (this.currentRow) {
      this.currentRowDays = this.currentRow.querySelectorAll('.main-weekday');
    }

    const targetRect = (clickTarget as HTMLElement).getBoundingClientRect();

    if (isLeftDrag) {
      this.updateElementOnLeftDrag(clickTarget as HTMLElement, this.currentRow, targetRect);
    } else {
      this.updateElementOnRightDrag(this.currentRow, targetRect);
    }

    this.currentRow.addEventListener('mousemove', e => this.onMove(e.clientX, this.plannerCardId), true);
  }

  touchResize(x: number, target: HTMLElement, isLeft: boolean) {
    this.resizing = true;
    this.startX = x;

    const clickTarget = target;
    const parentRow = clickTarget.parentElement;
    const parentRowWidth = parentRow?.offsetWidth;

    this.minWidth = parentRowWidth;

    this.plannerCardContents = document.querySelector(`#card-contents-${this.plannerCardId}`) as HTMLElement;
    this.currentRow = this.plannerCardContents.parentElement?.parentElement;

    // get day cells for current row being resized
    if (this.currentRow) {
      this.currentRowDays = this.currentRow.querySelectorAll('.main-weekday');
    }

    const targetRect = target.getBoundingClientRect();

    if (isLeft) {
      this.updateElementOnLeftDrag(target, this.currentRow, targetRect);
    } else {
      this.updateElementOnRightDrag(this.currentRow, targetRect);
    }

    this.currentRow.addEventListener('touchmove', e => this.onMove(e.touches[0]?.clientX, this.plannerCardId), true);
  }

  onMove(x: number, plannerCardId: string): void {
    if (this.resizing && this.resizingSide && plannerCardId) {
      this.resizableContainer = document.querySelector(`#card-contents-${plannerCardId}`);

      if (this.resizableContainer) {
        let newWidth: number;

        if (this.resizingSide === 'right') {
          newWidth = this.startWidth + (x - this.startX);
        } else {
          newWidth = this.startWidth - (x - this.startX);
        }

        this.resizableContainer.style.width = `${newWidth}px`;
        this.resizableContainer.style.borderLeftColor = this.borderColorLight;
        this.resizableContainer.style.borderRightColor = this.borderColorLight;

        const innerCard = this.resizableContainer.firstElementChild as HTMLElement;
        if (innerCard) {
          innerCard.style.backgroundColor = `${this.backgroundColorLight}`;
        }
      }
    }
  }

  // data-day-id is 0-index (0 is Monday, 3 is Thurdsay)
  onMouseUp = (x: number, plannerCardId: string): any => {
    // Adding verification that resizableContainer exists to fix clicks breaking things
    if (plannerCardId && this.resizableContainer) {
      const mouseUpPageXCoordinate = x;

      const resizableDiv = document.querySelector(`#card-contents-${plannerCardId}`) as HTMLElement;

      if (resizableDiv) {
        resizableDiv.classList.remove('moving');
        resizableDiv.style.position = 'absolute';
        resizableDiv.style.zIndex = '2';
      }

      let dayReleasedOn: string = null;

      this.currentRowDays?.forEach((day) => {
        const dayBoundingClient = day.getBoundingClientRect();

        if (dayBoundingClient.left < mouseUpPageXCoordinate && dayBoundingClient.right > mouseUpPageXCoordinate) {
          dayReleasedOn = day.getAttribute('data-day-id');
        }
      });

      // reset data if not released on a day
      if (!dayReleasedOn && resizableDiv && this.resizableContainer) {
        resizableDiv.style.width = 'revert-layer';
        resizableDiv.style.minWidth = 'revert-layer';

        this.resizableContainer.style.borderLeftColor = this.borderColorFull;
        this.resizableContainer.style.borderRightColor = this.borderColorFull;

        const innerCard = this.resizableContainer.firstElementChild as HTMLElement;
        if (innerCard) {
          innerCard.style.backgroundColor = `${this.backgroundColorFull}`;
        }
      }

      const mouseUpData =  {
        dayOfWeekReleasedOn: parseInt(dayReleasedOn, 10) + 1,
        rowUpdated: this.currentRow,
        resizedSide: this.resizingSide
      };

      this.removeEventListeners(plannerCardId);
      this.resetAllValues();

      return mouseUpData;
    }

    return null;
  };

  private removeEventListeners(plannerCardId: string) {
    if (plannerCardId && this.currentRow) {
      this.currentRow?.removeEventListener('mousemove', e => this.onMove(e.clientX, plannerCardId));
      this.currentRow?.removeEventListener('touchmove', e => this.onMove(e.touches[0]?.clientX, plannerCardId));

    }
  }

  private updateElementOnLeftDrag(target: HTMLElement, currentGridRow: HTMLElement, targetRect: DOMRect) {
    const rightEdgeOfCardSelected = targetRect.right;
    const parentContainerWidth = target['offsetParent']['clientWidth'];
    const elementRightPosition =  parentContainerWidth
      - (this.plannerCardContents.offsetLeft + this.plannerCardContents.offsetWidth);

    this.resizingSide = 'left';

    // data-day-id="0" = Monday
    const currentRowMondayContainer = currentGridRow.querySelector('[data-day-id="0"]');
    this.leftEdgeOfMonday = currentRowMondayContainer.getBoundingClientRect()?.left;
    this.maxWidth = rightEdgeOfCardSelected - this.leftEdgeOfMonday;

    this.startWidth = targetRect?.width || this.minWidthBuffer;
    this.plannerCardContents.style.left = 'auto';
    this.plannerCardContents.style.zIndex = '3';
    this.plannerCardContents.classList.add('moving');
    this.plannerCardContents.style.right = `${elementRightPosition}px`;
    this.plannerCardContents.style.height = `${targetRect.height}px`;
    this.plannerCardContents.style.width = `${this.startWidth}px`;
    this.plannerCardContents.style.minWidth = `${this.minWidth}px`;
    this.plannerCardContents.style.maxWidth = `${this.maxWidth}px`;
  }

  private updateElementOnRightDrag(currentGridRow: HTMLElement, targetRect: DOMRect) {
    const leftEdgeOfCardSelected = targetRect.left;
    // data-day-id="3" = Thursday
    const currentRowThursdayContainer = currentGridRow.querySelector('[data-day-id="3"]');

    this.resizingSide = 'right';
    this.startWidth = targetRect?.width || this.minWidthBuffer;

    this.rightEdgeOfFriday = currentRowThursdayContainer.getBoundingClientRect()?.right;
    this.maxWidth = this.rightEdgeOfFriday - leftEdgeOfCardSelected;

    this.plannerCardContents.classList.add('moving');
    this.plannerCardContents.style.left = '0';
    this.plannerCardContents.style.right = 'auto';
    this.plannerCardContents.style.zIndex = '3';
    this.plannerCardContents.style.height = `${targetRect.height}px`;
    this.plannerCardContents.style.width = `${this.startWidth}px`;
    this.plannerCardContents.style.minWidth = `${this.minWidth}px`;
    this.plannerCardContents.style.maxWidth = `${this.maxWidth}px`;
  }

  reset() {
    this.resetAllValues();
    this.backgroundColorFull = null;
    this.backgroundColorLight = null;
    this.borderColorFull = null;
    this.borderColorLight = null;
    this.resizableContainer = null;
  }
}
